// Shared group / sort / search controls that drive a ViewState.
//
// Phase 0 surfaces the common ClickUp-style controls: group-by, a single sort
// key + direction, and a text search (stored as a "text" filter rule). Richer
// composable filters (status/assignee/date chips) land in Phase 1/5 and append
// to the same `filters` array — this bar already round-trips them untouched.

import { ArrowDownAZ, ArrowUpAZ, Search } from "lucide-preact";
import { FilterDisclosure } from "./FilterDisclosure";
import {
  GROUP_BY_LABEL,
  type FilterOp,
  type GroupBy,
  type SortKey,
  type ViewState,
} from "../lib/viewState";
import { useWorkItemTypes } from "../store/workItemTypes";

const DUE_OPS: { value: "" | FilterOp; label: string }[] = [
  { value: "", label: "Alle Fälligkeiten" },
  { value: "overdue", label: "Überfällig" },
  { value: "today", label: "Heute fällig" },
  { value: "this_week", label: "Diese Woche" },
];

const SORT_LABEL: Record<SortKey, string> = {
  manual: "Manuell",
  title: "Titel",
  due: "Fälligkeit",
  priority: "Priorität",
  status: "Status",
  created: "Erstellt",
  updated: "Geändert",
};

interface Props {
  state: ViewState;
  onChange: (next: ViewState) => void;
  /** Group-by options to offer (project pages add "phase"; hub adds "project"). */
  groupByOptions: GroupBy[];
  /** Sort keys to offer. */
  sortOptions?: SortKey[];
  /**
   * Project scope for the type filter (#872). Only project-scoped surfaces
   * (a single project's task views) know a single projectId — the "Meine
   * Aufgaben" hub and personal Lists span sources with no work-item-type
   * table, so they omit this and the type filter control doesn't render.
   */
  projectId?: number;
}

/** Read the current "hide completed" state (#873: a `done` filter with value `false`). */
function hideDoneValue(state: ViewState): boolean {
  return state.filters.some((r) => r.field === "done" && r.value === false);
}

/**
 * Type filter (#872), split into its own component so `useWorkItemTypes` is
 * only invoked when this component is actually mounted (project-scoped
 * callers pass `projectId`; hub/personal-list callers don't render it at
 * all) — conditionally rendering a child component is fine, unlike
 * conditionally calling a hook inside one.
 */
/**
 * The two cross-view filter controls (#872 type, #873 hide-completed), extracted
 * so BOTH stacks render the same thing: `GroupSortFilterBar` (the personal /
 * cross-project hub) and `routes/Project.tsx`'s toolbar (the four project views).
 *
 * This exists because the first attempt put these controls only inside
 * GroupSortFilterBar, which `Project.tsx` never renders — so the type filter was
 * unreachable and hide-completed never reached a project view. Keep it ONE
 * component; a second copy is how that bug happened.
 */
export function FilterControls(props: {
  /** When set, the type filter is offered (types are per project). */
  projectId?: number;
  state: ViewState;
  onChange: (next: ViewState) => void;
}): preact.JSX.Element {
  const { state, onChange } = props;
  const hideDone = hideDoneValue(state);
  const toggleHideDone = (): void => {
    const others = state.filters.filter((r) => r.field !== "done");
    onChange({
      ...state,
      filters: hideDone
        ? others
        : [...others, { field: "done" as const, op: "is" as const, value: false }],
    });
  };
  return (
    <>
      {props.projectId !== undefined && (
        <TypeFilterControl projectId={props.projectId} state={state} onChange={onChange} />
      )}
      {/* default OFF — never hides data without an explicit user action */}
      <button
        type="button"
        onClick={toggleHideDone}
        aria-pressed={hideDone}
        class={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-medium min-h-[36px] sm:min-h-[28px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
          hideDone
            ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
            : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)]"
        }`}
      >
        Erledigte ausblenden
      </button>
    </>
  );
}

function TypeFilterControl(props: {
  projectId: number;
  state: ViewState;
  onChange: (next: ViewState) => void;
}): preact.JSX.Element | null {
  const { projectId, state, onChange } = props;
  const { items } = useWorkItemTypes(projectId);
  if (items.length === 0) return null;

  const typeValue = (() => {
    const f = state.filters.find((r) => r.field === "type");
    return typeof f?.value === "number" ? String(f.value) : "";
  })();
  const setType = (raw: string): void => {
    const others = state.filters.filter((r) => r.field !== "type");
    const next = raw ? [...others, { field: "type" as const, op: "is" as const, value: Number(raw) }] : others;
    onChange({ ...state, filters: next });
  };

  return (
    <label class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[36px] sm:min-h-[28px] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
      <span class="text-label uppercase tracking-wider">Typ</span>
      <select
        value={typeValue}
        onChange={(e) => setType((e.target as HTMLSelectElement).value)}
        class="min-w-0 flex-1 sm:flex-none bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none cursor-pointer"
        aria-label="Nach Typ filtern"
      >
        <option value="">Alle Typen</option>
        {items.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Read the current text filter value, if any. */
function textValue(state: ViewState): string {
  const f = state.filters.find((r) => r.field === "text");
  return typeof f?.value === "string" ? f.value : "";
}

export function GroupSortFilterBar(props: Props): preact.JSX.Element {
  const { state, onChange, groupByOptions } = props;
  const sortOptions = props.sortOptions ?? ["manual", "title", "due", "priority", "status", "updated"];
  const primarySort = state.sort[0] ?? { key: "manual" as SortKey, dir: "asc" as const };

  const setGroupBy = (gb: GroupBy): void => onChange({ ...state, groupBy: gb });
  const setSortKey = (key: SortKey): void => onChange({ ...state, sort: [{ key, dir: primarySort.dir }] });
  const toggleSortDir = (): void =>
    onChange({ ...state, sort: [{ key: primarySort.key, dir: primarySort.dir === "asc" ? "desc" : "asc" }] });
  const setText = (text: string): void => {
    const others = state.filters.filter((r) => r.field !== "text");
    const next = text.trim() ? [...others, { field: "text" as const, op: "contains" as const, value: text }] : others;
    onChange({ ...state, filters: next });
  };
  const dueOp = (state.filters.find((r) => r.field === "due")?.op ?? "") as "" | FilterOp;
  const setDue = (op: "" | FilterOp): void => {
    const others = state.filters.filter((r) => r.field !== "due");
    const next = op ? [...others, { field: "due" as const, op }] : others;
    onChange({ ...state, filters: next });
  };

  return (
    // On mobile the controls collapse behind the FilterDisclosure toggle; on
    // sm+ both wrappers are display:contents so the controls flow inline in
    // the parent toolbar row (all callers use flex flex-wrap gap-2).
    <FilterDisclosure count={state.filters.length}>
      {/* search */}
      <div class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 min-h-[36px] sm:min-h-[28px] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
        <Search size={14} class="text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
        <input
          type="text"
          value={textValue(state)}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
          placeholder="Suche…"
          class="min-w-0 flex-1 sm:flex-none sm:w-40 bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none"
          aria-label="Aufgaben durchsuchen"
        />
      </div>

      {/* group-by */}
      <label class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[36px] sm:min-h-[28px] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
        <span class="text-label uppercase tracking-wider">Gruppieren</span>
        <select
          value={state.groupBy}
          onChange={(e) => setGroupBy((e.target as HTMLSelectElement).value as GroupBy)}
          class="min-w-0 flex-1 sm:flex-none bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none cursor-pointer"
          aria-label="Gruppieren nach"
        >
          {groupByOptions.map((g) => (
            <option key={g} value={g}>
              {GROUP_BY_LABEL[g]}
            </option>
          ))}
        </select>
      </label>

      {/* due filter */}
      <label class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[36px] sm:min-h-[28px] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
        <span class="text-label uppercase tracking-wider">Fällig</span>
        <select
          value={dueOp}
          onChange={(e) => setDue((e.target as HTMLSelectElement).value as "" | FilterOp)}
          class="min-w-0 flex-1 sm:flex-none bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none cursor-pointer"
          aria-label="Nach Fälligkeit filtern"
        >
          {DUE_OPS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <FilterControls projectId={props.projectId} state={state} onChange={onChange} />

      {/* sort */}
      <label class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[36px] sm:min-h-[28px] focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
        <span class="text-label uppercase tracking-wider">Sortieren</span>
        <select
          value={primarySort.key}
          onChange={(e) => setSortKey((e.target as HTMLSelectElement).value as SortKey)}
          class="min-w-0 flex-1 sm:flex-none bg-transparent text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none cursor-pointer"
          aria-label="Sortieren nach"
        >
          {sortOptions.map((s) => (
            <option key={s} value={s}>
              {SORT_LABEL[s]}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={toggleSortDir}
          class="inline-flex items-center justify-center size-6 rounded hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          aria-label={primarySort.dir === "asc" ? "Aufsteigend (klicken für absteigend)" : "Absteigend (klicken für aufsteigend)"}
        >
          {primarySort.dir === "asc" ? <ArrowUpAZ size={14} aria-hidden="true" /> : <ArrowDownAZ size={14} aria-hidden="true" />}
        </button>
      </label>
    </FilterDisclosure>
  );
}
